import { Injectable } from "@angular/core";
import { BaseApiService } from "../base-api.service";
import { HttpClient } from "@angular/common/http";
import { delay, firstValueFrom, map, Observable } from "rxjs";
import { environment } from "@beyou/environments/environment";
import { LLMInstruction, LLMInstructionBase, LLMInstructionCode, LLMInstructionTranslation } from "../../models/llm-instruction.model";
import { LLMInstructionDto, LLMInstructionBaseDto, LLMInstructionCodesDto } from "../../dto/llm-instructions/lm-instruction.dto";
import { LLMInstructionMapper } from "../../mappers/llm-instructions/llm-instruction.mapper";
import { LLMInstructionTranslationMapper } from "../../mappers/llm-instructions/llm-instruction-translation.mapper";
import { LLMInstructionTranslationsDto } from "../../dto/llm-instructions/lm-instruction-translations.dto";
import { LLMInstructionCodeMapper } from "../../mappers/llm-instructions/llm-instruction-code.mapper";
import { ExportResultDto } from "../../dto/export-result.dto";

@Injectable({
  providedIn: 'root'
})
export class LLMInstructionsApiService extends BaseApiService {

  constructor(http: HttpClient) {
    super(http);
  }

  public async getAllCodes(): Promise<LLMInstructionCode[]> {
    const apiUrl = `${environment.adminApiBaseUrl}/llm-instructions/codes`;
    return await firstValueFrom(this.http.get<LLMInstructionCodesDto>(apiUrl).pipe(
      map((response) => {
        return response.instruction_codes.map(item => {
          return LLMInstructionCodeMapper.toModel(item);
        })
      })
    ));
  }

  public getAll(): Observable<LLMInstruction[]> {
    const apiUrl = `${environment.adminApiBaseUrl}/llm-instructions`;
    return this.getAllPagedData<LLMInstructionDto>(apiUrl).pipe(
      map((response) => {
        return response.map(item => {
          return LLMInstructionMapper.toModel(item);
        })
      })
    );
  }

  public async add(LLMInstruction: LLMInstructionBase): Promise<LLMInstruction> {
    const apiUrl = `${environment.adminApiBaseUrl}/llm-instructions`;
    const dto = LLMInstructionMapper.toCreateDto(LLMInstruction);
    console.debug('new message template dto', dto);
    return await firstValueFrom(this.http.post<LLMInstructionDto>(apiUrl, dto).pipe(
      map((response) => {
        console.debug('LLM instruction added: ', response.id);
        return LLMInstructionMapper.toModel(response);
      })
    ));
  }

  public async update(LLMInstruction: LLMInstruction): Promise<LLMInstruction> {
    const apiUrl = `${environment.adminApiBaseUrl}/llm-instructions/${LLMInstruction.id}`;
    const dto = LLMInstructionMapper.toUpdateDto(LLMInstruction);
    return await firstValueFrom(this.http.put<LLMInstructionDto>(apiUrl, dto).pipe(
      map((response) => {
        console.debug('LLM instruction updated: ', response.id);
        return LLMInstructionMapper.toModel(response);
      })
    ));
  }

  public async delete(id: number): Promise<void> {
    const apiUrl = `${environment.adminApiBaseUrl}/llm-instructions/${id}`;
    try {
      await firstValueFrom(this.http.delete(apiUrl));
      console.log('LLM instruction deleted: ', id);
    } catch (error) {
      console.error('Error deleting LLM instruction with id: ', id, error);
      throw error;
    }
  }

  public async export(languageId: number): Promise<ExportResultDto> {
    const apiUrl = `${environment.adminApiBaseUrl}/llm-instructions/export?language_id=${languageId}`;
    return await firstValueFrom(this.http.get<ExportResultDto>(apiUrl));
  }

  public async import(file: File): Promise<void> {
    const uploadData = new FormData();
    uploadData.append('file', file);
    const apiUrl = `${environment.adminApiBaseUrl}/llm-instructions/import`;
    await firstValueFrom(this.http.post(apiUrl, uploadData));
  }

  public async getTranslations(id: number): Promise<LLMInstructionTranslation[]> {
    const apiUrl = `${environment.adminApiBaseUrl}/llm-instructions/${id}/translations`;
    return await firstValueFrom(this.http.get<LLMInstructionTranslationsDto>(apiUrl).pipe(
      map((response) => {
        console.debug('LLM instruction translations retrieved: ', id);
        return response.translations.map(item => LLMInstructionTranslationMapper.toModel(item));
      })
    ));
  }

  public async updateTranslations(id: number, translations: LLMInstructionTranslation[]): Promise<void> {
    const apiUrl = `${environment.adminApiBaseUrl}/llm-instructions/${id}/translations`;
    const dto = {
      translations: translations.map(item => LLMInstructionTranslationMapper.toDto(item))
    };
    await firstValueFrom(this.http.post(apiUrl, dto));
    console.debug('LLM instruction translations updated: ', id);
  }
}
